import React, { useReducer, useEffect } from "react";
import { DestinationType } from "../../../../../lib/app/constants";

import { getUser } from "../../../state/User";
import { formatDestination } from "../../helpers";
import { closePopUp, showPopUp } from "../../utils/Overlay";
import Close from "../../utils/icons/Close";
import ContentPreviewCompact from "../ContentPreviewCompact";
import ContentVerification from "../ContentVerification";

const TOGGLE_DESTINATION = "TOGGLE_DESTINATION";
const TOGGLE_CONTENT = "TOGGLE_CONTENT";
const SET = "SET";

export default function PickDestinations({
  onSelect,
  content,
  campaign,
}: {
  onSelect: Function;
  content: Content[];
  campaign: Post;
}) {
  const user = getUser();
  const [mapping, setMapping] = useReducer(mappingReducer, []);
  const destinations = user ? user.destinations || [] : [];
  const fulfilled = mapping.find(
    (map) => map.content && map.content.length > 0
  );

  useEffect(() => {
    // synching the mapped items with the actual content
    (mapping as SchedulerMapping[]).forEach((map) => {
      map.content.forEach((cId) => {
        if (!content.find((c) => c.id === cId)) {
          setMapping({
            type: TOGGLE_CONTENT,
            destinationId: map.destinationId,
            content: cId,
          });
        }
      });
    });
  }, [content ? content.length : 0]);

  useEffect(() => {
    onSelect(
      mapping.filter((item) => item.isSelected && item.content.length > 0)
    );
  }, [JSON.stringify(mapping)]);

  return (
    <div className={"scheduler-phase " + (fulfilled ? "fulfilled" : "")}>
      {mapping.length > 0 && (
        <div className="mb1 grid grid2">
          {(mapping as SchedulerMapping[])
            .filter((item) => item.isSelected)
            .map((item, i) => {
              return (
                <div key={i}>
                  <div className="tal">
                    <div className="mb05">
                      {formatDestination(
                        destinations.find((d) => d.id === item.destinationId),
                        "text",
                        true
                      )}
                    </div>
                    {content.map((c) => {
                      const isActivated =
                        item.content &&
                        item.content.find((cId) => cId === c.id);
                      return (
                        <button
                          className={
                            "block reset as-link with-icon mb03 tal " +
                            (isActivated ? "activated" : "deactivated op08")
                          }
                          key={c.id}
                          onClick={() => {
                            setMapping({
                              type: TOGGLE_CONTENT,
                              destinationId: item.destinationId,
                              content: c.id,
                            });
                          }}
                        >
                          <ContentPreviewCompact
                            content={c}
                            key={c.id}
                            prefix={""}
                            noLink
                            className=""
                          />
                        </button>
                      );
                    })}
                  </div>
                  <div>
                    <ContentVerification
                      content={item.content
                        .map((id) => campaign.content?.find((c) => c.id === id))
                        .filter(Boolean)}
                      destination={destinations.find(
                        (d) => d.id === item.destinationId
                      )}
                    />
                  </div>
                </div>
              );
            })}
        </div>
      )}
      <div className={mapping.length === 0 ? "tac" : ""}>
        <button
          className={mapping.length > 0 ? "small" : "primary mt2 mb1"}
          onClick={() => {
            showPopUp(
              <Mapper
                content={content.map((c) => c.id)}
                destinations={destinations}
                currentMapping={mapping}
                onMap={(newMapping) => {
                  closePopUp();
                  setMapping({ type: SET, mapping: newMapping });
                }}
              />,
              {
                width: "520px",
              }
            );
          }}
        >
          {mapping.length > 0 ? "Manage destinations" : "Pick destination"}
        </button>
      </div>
    </div>
  );
}

function Mapper({
  content,
  destinations,
  onMap,
  currentMapping,
}: {
  content: string[];
  destinations: Destination[];
  onMap: Function;
  currentMapping: SchedulerMapping[];
}) {
  const [mapping, setMapping] = useReducer(mappingReducer, currentMapping);

  function getButtonClass(id: string) {
    return (
      "reset as-link ib with-icon tal " +
      (mapping.find(
        ({ destinationId, isSelected }) => destinationId === id && isSelected
      )
        ? "activated"
        : "deactivated")
    );
  }

  return (
    <div className="rel">
      <button
        className="reset as-link abs"
        style={{ top: "-29px", right: "-27px" }}
        onClick={() => closePopUp()}
      >
        <Close />
      </button>
      <div className="fixed-height-400" style={{ minHeight: "400px" }}>
        {destinations
          .filter((d) => d.type === DestinationType.GROUP)
          .map((d) => {
            return (
              <div key={d.id} className="mb05">
                <button
                  className="reset as-link ib with-icon"
                  onClick={() => {
                    d.meta.destinationIds.forEach((destinationId) => {
                      setMapping({
                        type: TOGGLE_DESTINATION,
                        destinationId,
                        content,
                      });
                    });
                  }}
                >
                  {formatDestination(d, "text", true)}
                </button>
              </div>
            );
          })}
        <hr />
        {destinations
          .filter((d) => d.type !== DestinationType.GROUP)
          .map((d) => {
            return (
              <div key={d.id} className="mb05">
                <button
                  className={getButtonClass(d.id)}
                  onClick={() =>
                    setMapping({
                      type: TOGGLE_DESTINATION,
                      destinationId: d.id,
                      content,
                    })
                  }
                >
                  {formatDestination(d, "text", true)}
                </button>
              </div>
            );
          })}
      </div>
      <div className="tac mt1">
        <button className="secondary" onClick={() => onMap(mapping)}>
          Use the selected destinations
        </button>
      </div>
    </div>
  );
}

function mappingReducer(
  state: SchedulerMapping[],
  action: {
    type: string;
    destinationId: string;
    content?: string | string[];
    mapping?: SchedulerMapping[];
  }
) {
  let currentState = [...state];
  switch (action.type) {
    case TOGGLE_DESTINATION:
      const existingDestination = state.find(
        (item) => item.destinationId === action.destinationId
      );
      if (!existingDestination) {
        currentState.push({
          destinationId: action.destinationId,
          isSelected: true,
          content: (action.content as string[]) || [],
        });
      } else {
        existingDestination.isSelected = !existingDestination.isSelected;
      }
      break;
    case TOGGLE_CONTENT:
      const destination = state.find(
        (item) => item.destinationId === action.destinationId
      );
      if (destination) {
        const contentItem = destination.content.find(
          (cId) => cId === action.content
        );
        if (!contentItem) {
          destination.content.push(action.content as string);
        } else {
          destination.content = destination.content.filter(
            (cId) => cId !== action?.content
          );
        }
      } else {
        currentState.push({
          destinationId: action.destinationId,
          isSelected: true,
          content: [action.content as string],
        });
      }
      break;
    case SET:
      currentState = action.mapping as SchedulerMapping[];
      break;
  }
  return currentState;
}
